<script>
import AdminPanel from '@components/admin-panel/admin-panel.vue';
import CardMealPlanMinimal from '@components/cards/card-meal-plan-minimal.vue';
import MealPlanForm from '@components/forms/meal-plan-form.vue';
import NoSearchResults from '@components/notifications/no-search-results.vue';
import RecipePreview from '@components/recipe/recipe-preview.vue';
import SearchInput from '@components/search-input/search-input.vue';
import SubheaderActions from '@components/subheader/subheader-actions.vue';
import { adminPanelMixin } from '@mixins/admin-panel-mixin';
import { platelistModelMixin } from '@mixins/models/platelist-model-mixin';
import { cloneDeep } from 'lodash';
import {
  adminPanelComputed,
  adminPanelMethods,
  authComputed,
  authMethods,
  platelistComputed,
  platelistMethods,
} from '@state/helpers';

export default {
  page: {
    title: 'Recipes',
    meta: [{ name: 'description', content: 'The Recipes view.' }],
  },
  components: {
    AdminPanel,
    CardMealPlanMinimal,
    MealPlanForm,
    NoSearchResults,
    RecipePreview,
    SearchInput,
    SubheaderActions,
  },
  mixins: [adminPanelMixin, platelistModelMixin],
  data() {
    return {
      isLoadingPlatelists: false,
      placeholder: require('@assets/images/placeholder-photo-upload.svg'),
      searchVal: null,
      selectedFilters: [],
      supportEmail: this.$store.getters[
        'organization/organizationSupportEmail'
      ],
      searchComponentKey: 0,
      searchOpts: { keys: ['title'] },
      initialMealList: null,
    };
  },
  computed: {
    ...authComputed,
    ...adminPanelComputed,
    ...platelistComputed,
    filters() {
      return this.platelistFilter;
    },
    mealPlans() {
      let platelists = this.allPlatelists;
      if (
        this.filters &&
        this.filters.library &&
        this.filters.library.length &&
        this.filters.library.length === 1
      ) {
        platelists = this.allPlatelists.filter((plate) => {
          return (
            (this.filters.library[0] === 1 && plate.editabilityStatus.locked) ||
            (this.filters.library[0] === 0 && !plate.editabilityStatus.locked)
          );
        });
      }
      if (this.filters && this.filters.status && this.filters.status.length) {
        platelists = platelists.filter((plate) => {
          const publishStatus = plate.draft
            ? 'draft'
            : plate.archived
            ? 'archived'
            : 'published';
          if (this.filters.status.includes(publishStatus)) {
            return true;
          }
        });
      }
      return platelists;
    },
  },
  async created() {
    if (!this.allPlatelists || !this.allPlatelists.length) {
      this.isLoadingPlatelists = true;
      await this.getAllPlatelists();
      this.isLoadingPlatelists = false;
    }

    if (this.currentUser && !this.currentUser.org) {
      await this.getOrg(this.userOrgId);
    }
    this.initialMealList = cloneDeep(this.allPlatelists);
  },
  destroyed() {
    this.clearFilters();
  },
  methods: {
    ...authMethods,
    ...adminPanelMethods,
    ...platelistMethods,
    addPlatelistPlaceholder() {
      const platelistHolder = {
        ...this.$_getPlatelistModel(),
        author: 'Author',
        imageurl: this.placeholder,
        title: 'New Plan',
      };
      this.setNewPlatelistPlaceholder(platelistHolder);
    },
    async showAdminPanel(component, data = null, size = '') {
      this.setDiscardPlatelistChanges(false);
      this.addPlatelistPlaceholder();
      await this.setPanelContent(this.getPanelContent(component, data, size));
      this.setShowAdminPanel();
    },
    async clearFilters() {
      this.selectedFilters = [];

      await this.setClearMealPlanFilters(!this.clearMealPlanFilters);
      if (this.mealPlans.length === 0) {
        this.setAllPlatelists(cloneDeep(this.initialMealList));
      }
      this.searchComponentKey++;
    },
    onFilterResults(val) {
      this.setAllPlatelists(val);
    },
    onHidePanel() {
      if (this.hasUnsavedPlatelistChanges && !this.panelContentDetail) {
        this.$router.app.$bvModal.show('unsaved-changes-modal');
      } else {
        this.setRemovePlatelistPlaceholder(true);
      }
    },
  },
  watch: {
    discardPlatelistChanges(newVal, oldVal) {
      if (newVal) {
        this.setRemovePlatelistPlaceholder(true);
      }
    },
  },
};
</script>

<template>
  <div class="meal-plans">
    <AdminPanel
      :title="panelContent ? panelContent.title : ''"
      @hide-panel="onHidePanel"
    >
      <component
        v-if="panelContent"
        :is="panelContent.component"
        :content="panelContent"
      />
    </AdminPanel>

    <div class="content-wrapper">
      <SubheaderActions>
        <SearchInput
          :key="searchComponentKey"
          message="Search Plans"
          :square="true"
          :options="searchOpts"
          :list="initialMealList"
          @filter-results="onFilterResults"
        />
        <BaseButton
          type="button"
          class="btn-primary btn-round"
          @click="showAdminPanel('MealPlanForm')"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-cal-alt'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.4 0v1.2H3.6V0H2.4v1.2h-.6c-.666 0-1.194.54-1.194 1.2L.6 10.8c0 .66.534 1.2 1.2 1.2h8.4c.66 0 1.2-.54 1.2-1.2V2.4c0-.66-.54-1.2-1.2-1.2h-.6V0H8.4zM9 6.6H6v3h3v-3zm-7.2 4.2h8.4V4.2H1.8v6.6z" fill="#fff"/></svg>
          Create New Plan
        </BaseButton>
      </SubheaderActions>

      <div class="loading-wrapper" v-if="isLoadingPlatelists">
        <BaseLoadingSpinner />
      </div>

      <NoSearchResults
        v-if="!mealPlans.length && !isLoadingPlatelists"
        @clear-search-filters="clearFilters"
      >
        <p slot="subtext">
          This plan does not exist in the meal plans Library. If you want to use
          this recipe, you will need to create it.
        </p>
        <BaseButton
          slot="action-btn"
          type="button"
          class="btn-primary btn-round"
          @click="showAdminPanel('MealPlanForm')"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-cal-alt'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.4 0v1.2H3.6V0H2.4v1.2h-.6c-.666 0-1.194.54-1.194 1.2L.6 10.8c0 .66.534 1.2 1.2 1.2h8.4c.66 0 1.2-.54 1.2-1.2V2.4c0-.66-.54-1.2-1.2-1.2h-.6V0H8.4zM9 6.6H6v3h3v-3zm-7.2 4.2h8.4V4.2H1.8v6.6z" fill="#fff"/></svg>
          Create New
        </BaseButton>
      </NoSearchResults>

      <div class="grid-cards">
        <CardMealPlanMinimal
          v-for="item in mealPlans"
          :key="item._id"
          :platelist="item"
          :orgName="currentUser.org.name"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.icon-calendar {
  path {
    fill: black;
  }
}

.icon-cal-alt {
  height: 1.6rem;
  width: 1.6rem;
  path {
    fill: white;
  }
}

.loading-wrapper {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 30vh;
  margin-top: 35vh;
  width: 100%;

  ::v-deep .loading {
    height: 4rem;
    width: 4rem;
  }
}

.grid-cards {
  @include grid-cards(3, 23);
}
</style>
